import type { TKey } from './types';

const fr: Record<TKey, string> = {
  'nav.library':            'Bibliothèque',
  'nav.myBooks':            'Mes livres',
  'nav.credentials':        'Certificats',
  'nav.mindmap':            'Carte mentale',
  'nav.reading':            'Lecture',
  'nav.upload':             'Upload',
  'nav.signin':             'Sign in',
  'nav.signout':            'Sign out',
  'nav.localeSelect':       'Language',

  'brand.sub':              'BookQuest',
  'onboard.welcome':        'Start with one book.',
  'onboard.welcomeSub':     'Pick a curated title or upload your own PDF — receive a four-layer mindmap, nine-tier adaptive quiz, and lifelong credentials.',
  'onboard.featureMapTitle':  'Four-layer mindmap',
  'onboard.featureMapBody':   'From surface narrative (L1) to the unsaid premises (L4) — a knowledge map drawn across four layers by AI.',
  'onboard.featureQuizTitle': 'Nine-tier adaptive quiz',
  'onboard.featureQuizBody':  'IRT-based difficulty (T1–T9) adjusts to your understanding in real time.',
  'onboard.featureCredTitle': 'Lifelong credentials',
  'onboard.featureCredBody':  'Every completion is issued as a W3C Verifiable Credential — externally verifiable, kept for life.',
  'onboard.ctaBrowse':      'Browse curated books',
  'onboard.ctaUpload':      'Upload my PDF',
  'onboard.signedOutHint':  'Sign in first to save your progress.',
  'onboard.ctaSignIn':      'Sign in / Sign up',
  'common.loading':         'Chargement…',
  'common.offline':         'hors ligne',
  'common.free':            'Gratuit',
  'common.error':           'erreur',
  'common.back':            '← Bibliothèque',
  'common.continue':        'Continuer',
  'common.submit':          'Envoyer',
  'common.skip':            'Passer',
  'common.next':            'Suivant',
  'common.finish':          'Terminer',
  'common.startAssessment': 'Démarrer l’évaluation',
  'theme.label':            'Thème',
  'theme.light':            'Clair',
  'theme.dark':             'Sombre',
  'theme.system':           'Système',
  'sound.label':            'Son',
  'sound.on':               'Activé',
  'sound.off':              'Désactivé',
  'api.error.badRequest':   'La requête a été rejetée.',
  'api.error.unauthorized': 'Veuillez vous connecter.',
  'api.error.forbidden':    'Vous n’avez pas accès à cette ressource.',
  'api.error.notFound':     'Introuvable — la ressource a peut-être été déplacée ou supprimée.',
  'api.error.tooLarge':     'Fichier trop volumineux. Réessayez avec moins de 100 Mo.',
  'api.error.unsupported':  'Format de fichier non pris en charge.',
  'api.error.rateLimited':  'Trop de requêtes — réessayez dans un instant.',
  'api.error.server':       'Erreur serveur — réessayez bientôt.',
  'api.error.network':      'Connexion réseau perdue.',
  'api.error.unknown':      'Erreur inconnue',
  'a11y.skipToMain':        'Aller au contenu principal',
  'notFound.kicker':        'Introuvable · 404',
  'notFound.titleLine1':    'Ce chemin',
  'notFound.titleLine2':    'n’est pas sur l’étagère.',
  'notFound.lead':          'Le livre a été déplacé, ou cette page n’a jamais existé. Revenez à la bibliothèque pour recommencer.',
  'notFound.pathLabel':     'Chemin demandé',
  'notFound.cta':           'Vers la bibliothèque',
  'footer.tagline':         'Une bibliothèque où chaque livre devient carte mentale et certification',
  'footer.navLabel':        'Navigation du pied',
  'footer.about':           'À propos',
  'footer.privacy':         'Confidentialité',
  'footer.terms':           'Conditions',
  'footer.contact':         'Contact',
  'reader.controls':              'Commandes du lecteur',
  'reader.size':                  'Taille',
  'reader.theme':                 'Thème',
  'reader.previewFallback':       'L’aperçu du corps n’est pas encore prêt. Il apparaît une fois le livre analysé.',
  'reader.stubNotice':            'Aperçu de l’environnement de lecture. Le corps complet arrive à la prochaine version.',
  'reader.demoBody1':             'Concepts clés, contre-arguments, définitions et actions sont marqués par couleur pendant la lecture.',
  'reader.demoBody2':             'Les termes sont définis sur place,',
  'reader.demoBody3':             'et ce que le lecteur doit tenter est',
  'reader.demoHighlightYellow':   'un concept clé',
  'reader.demoHighlightCoral':    'un contre-argument',
  'reader.demoHighlightBlue':     'une définition',
  'reader.demoHighlightMint':     'une action',
  'reader.aside.legendLabel':     'Légende des surlignages',
  'reader.aside.legendYellow':    'Concept clé',
  'reader.aside.legendCoral':     'Contre-argument',
  'reader.aside.legendBlue':      'Définition',
  'reader.aside.legendMint':      'Action',
  'reader.aside.metaLabel':       'Livre',
  'common.correct':         'Correct.',
  'common.notQuite':        'Pas tout à fait.',
  'common.save':            'Garder pour plus tard',
  'common.beginReading':    'Commencer la lecture',
  'common.openMindmap':     'Ouvrir la carte mentale',
  'common.searchPlaceholder': 'Rechercher par titre, auteur, ISBN…',

  'library.kicker':         'Collection inaugurale · 100 vols',
  'library.heroLine1':      '100 pour commencer,',
  'library.heroLine2':      'une étagère sans fin.',
  'library.heroSub':        'La collection inaugurale — chaque livre renaît en carte mentale à quatre couches et en évaluation à neuf niveaux.',
  'library.featured':       'À l’affiche ce mois-ci',
  'library.curatedCount':   'sélectionnés',
  'library.noBooks':        'Aucun livre encore édité. Utilisez le script de seed ou la curation administrateur.',
  'library.filterAll':      'Tout',
  'library.filterClassics': 'Classiques',
  'library.filterPhilosophy': 'Philosophie',
  'library.filterK5':       'K-5',
  'library.filter68':       '6-8',
  'library.filter912':      '9-12',

  'detail.previewNodes':    'Aperçu de la carte mentale à quatre couches.',
  'detail.sampledQuestions': 'Trois questions, en aperçu.',
  'detail.bookMissing':     'Métadonnées du livre manquantes.',
  'detail.noQuestions':     'Pas encore de questions — le pipeline est peut-être en cours de traitement.',
  'detail.byAuthor':        'par',
  'detail.sourceLabel':            'Source',
  'detail.firstQuoteLabel':        'Première citation',
  'detail.relatedCredentialLabel': 'Certification associée',
  'detail.relatedCredentialBody':  'Ce livre débloque une certification de niveau Major à la fin.',
  'detail.pages':           'pages',
  'detail.metaSeparator':   ' · ',

  'map.fourLayers':         'Quatre couches',
  'map.navigation':         'Navigation',
  'map.surface':            'L1 Surface',
  'map.argument':           'L2 Argument',
  'map.concept':            'L3 Concept',
  'map.meta':               'L4 Méta — le non-dit',
  'map.notReady':           'Carte pas prête — le pipeline est peut-être en cours de traitement.',
  'map.loadingMap':         'Chargement de la carte…',
  'map.selectNode':         'Sélectionnez un nœud à inspecter',
  'map.selectNodeSub':      'Chaque nœud porte une citation (L1), un argument (L2), un concept (L3) ou une prémisse silencieuse (L4).',
  'map.hoverHint':          'Survolez un nœud',

  'quiz.of':                ' sur ',
  'quiz.minRemaining':      'min restantes',
  'quiz.noQuestions':       'Aucune question disponible.',
  'quiz.tier':              'Niveau',
  'quiz.celebration.kicker':         'Terminé',
  'quiz.celebration.titleLine1':     'Un livre',
  'quiz.celebration.titleLine2':     'achevé.',
  'quiz.celebration.lead':           'Votre évaluation est scellée en certification — conservée à vie.',
  'quiz.celebration.correct':        'Bonnes',
  'quiz.celebration.theta':          'θ final',
  'quiz.celebration.viewCredential': 'Voir la certification',

  'creds.kicker':           'Émis',
  'creds.heroLine':         'L’étagère de vos',
  'creds.heroEm':           'lectures gagnées',
  'creds.heroSub':          'Chaque étape émise comme W3C Verifiable Credentials — vérifiable de l’extérieur, conservé à vie.',
  'creds.master':           'Master ★',
  'creds.recent':           'Récemment émis.',
  'creds.pastDays':         '30 derniers jours',
  'creds.noneYet':          'Terminez un livre pour gagner votre premier certificat.',
  'creds.urn':              'URN',
  'creds.issued':           'Émis',
  'creds.anchor':           'Ancre',
  'creds.tier.micro':       'Micro',
  'creds.tier.standard':    'Standard',
  'creds.tier.major':       'Majeur',
  'creds.tier.master':      'Maître',
  'creds.tier.legendary':   'Légendaire',
  'creds.count':            'certificats',
  'creds.aside.tiersLabel':    'Échelle à 5 niveaux',
  'creds.aside.tiersBody':     'Micro → Standard → Major → Master → Legendary. 80 à 100 obtenues sur une vie.',
  'creds.aside.verifyLabel':   'Vérification externe',
  'creds.aside.verifyBody':    'Chaque URN suit le standard W3C Verifiable Credentials — vérifiable par tout vérificateur compatible.',
  'creds.aside.standardLabel': 'Standard',
  'creds.justIssued':          'À l’instant',
  'creds.copied':              'Copié',
  'creds.copyFailed':          'Échec de la copie',
  'creds.share':               'Partager',
  'creds.verifyExternally':    'Vérifier en externe',
  'creds.verifierComing':      'Portail de vérification bientôt disponible.',
  'creds.shareNative':         'Partager',
  'creds.print':               'Imprimer · PDF',
  'source.curated':         'éditorial',
  'source.user_upload':     'téléversement',
  'source.partner':         'partenaire',
  'source.public_domain':   'domaine public',
  'source.institution':     'institution',
  'auth.signinTitle':       'Sign in',
  'auth.signupTitle':       'Sign up',
  'auth.magicTitle':        'Magic link',
  'auth.email':             'Email',
  'auth.password':          'Password',
  'auth.signin':            'Sign in',
  'auth.signup':            'Create account',
  'auth.sendMagic':         'Send magic link',
  'auth.switchToSignup':    'New here? Create account',
  'auth.switchToSignin':    'Already have an account? Sign in',
  'auth.switchToMagic':     'Use magic link instead',
  'auth.notConfiguredBanner': 'Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in packages/web-app/.env.local.',
  'auth.checkEmail':        'Check your email to confirm your account.',
  'auth.magicSent':         'Magic link sent — check your email.',
  'auth.working':           'Working…',
  'auth.aside.whyLabel':       'Pourquoi BookQuest',
  'auth.aside.why1':           'Un livre devient une carte mentale à 4 couches.',
  'auth.aside.why2':           'Une évaluation à 9 niveaux mesure la lecture.',
  'auth.aside.why3':           'Les certifications s’accumulent à vie.',
  'auth.aside.privacyLabel':   'Confidentialité',
  'auth.aside.privacyBody':    'Vos données de lecture vous appartiennent.',

  'upload.title':           'Upload a book',
  'upload.help':            'PDFs only. The system de-duplicates globally — if anyone has already processed this file you get instant free access.',
  'upload.fileLabel':       'PDF file',
  'upload.countryLabel':    'Country (for pricing)',
  'upload.submit':          'Upload',
  'upload.submitting':      'Uploading…',
  'upload.chooseFirst':     'Choose a PDF first.',
  'upload.failed':          'Upload failed',
  'upload.resultTitle':     'Result',
  'upload.dedupeStatus':    'De-dup status',
  'upload.paymentStatus':   'Payment status',
  'upload.amount':          'Amount',
  'upload.jobId':           'Job ID',
  'upload.eta':             'ETA',
  'upload.watchProgress':   'Watch progress',
  'job.title':              "Progression de l'analyse",
  'job.processing':         'Analyse du livre et génération des questions…',
  'job.stage':              'Étape',
  'job.cost':               'Coût actuel',
  'job.done':               'Terminé',
  'job.failed':             "Une erreur s'est produite pendant le traitement",
  'job.openBook':           'Aller au livre',
  'job.eta':                "~20 min en général. Continue en arrière-plan si vous fermez.",
  'upload.aside.formatLabel':  'Formats pris en charge',
  'upload.aside.formatBody':   'PDF avec texte extractible. Jusqu’à 50 Mo.',
  'upload.aside.etaLabel':     'Durée estimée',
  'upload.aside.etaBody':      '5 à 15 minutes, selon la longueur et la file.',
  'upload.aside.privacyLabel': 'Confidentialité',
  'upload.aside.privacyBody':  'Les PDF téléversés sont supprimés après analyse.',
  'nav.achievements':       'Succès',
  'nav.admin':              'Admin',
  'gam.kicker':             'Votre parcours',
  'gam.heroLine':           'Lisez, résolvez,',
  'gam.heroEm':             'progressez',
  'gam.heroSub':            'Chaque question résolue et chaque livre terminé rapporte des XP et des badges. Montez en niveau et marquez le classement de votre nom.',
  'gam.levelLabel':         'Niveau',
  'gam.xpTotal':            'XP total',
  'gam.xpUnit':             'XP',
  'gam.toNext':             'avant le niveau suivant',
  'gam.maxLevel':           'Niveau max',
  'gam.badges':             'Badges',
  'gam.badgesSub':          "Les traces de votre apprentissage",
  'gam.locked':             'Verrouillé',
  'gam.noBadges':           'Pas encore de badge — commencez par votre première question.',
  'gam.leaderboard':        'Classement',
  'gam.leaderboardSub':     'Classement XP',
  'gam.rank':               'Rang',
  'gam.you':                'Vous',
  'gam.emptyBoard':         'Pas encore de classement.',
  'gam.period.daily':       "Aujourd'hui",
  'gam.period.weekly':      'Cette semaine',
  'gam.period.monthly':     'Ce mois',
  'gam.period.all':         'Tout',
  'gam.howXp':              "Comment fonctionne l'XP",
  'gam.howXpSub':           "Voici comment l'XP s'accumule",
  'gam.levelCurve':         'Courbe de niveaux',
  'gam.levelN':             'Niv',
  'gam.aside.label':        "Qu'est-ce que l'XP ?",
  'gam.aside.body':         "L'XP est le registre cumulé de votre apprentissage. Bonnes réponses, étapes terminées et livres achevés s'additionnent en niveaux.",
  'gam.aside.fairLabel':    'Gain équitable',
  'gam.aside.fairBody':     "Répondre à nouveau à la même question ne rapporte de l'XP qu'une fois, à la première bonne réponse.",
  'gam.xpRule.answer_correct':     'Bonne réponse',
  'gam.xpRule.stage_complete':     'Étape terminée',
  'gam.xpRule.book_clear':         'Livre terminé',
  'gam.xpRule.cr_submit':          'Réponse rédigée',
  'gam.xpRule.streak_day':         'Série quotidienne',
  'gam.xpRule.question_propose':   'Proposer une question',
  'gam.xpRule.question_adopted':   'Question adoptée',
  'gam.xpRule.review_participate': 'Participation à la revue',
  'detail.propose':            'Proposer une question',
  'prop.title':                'Proposer une question',
  'prop.help':                 'Rédigez une question à choix multiple pour ce livre. Si un administrateur l’adopte, elle rejoint la banque de questions et vous gagnez des XP et un badge.',
  'prop.stemLabel':            'Question (énoncé)',
  'prop.stemPlaceholder':      'ex. Pourquoi le protagoniste part-il dans ce chapitre ?',
  'prop.choicesLabel':         'Choix (sélectionnez la bonne réponse)',
  'prop.choiceCorrect':        'Correct',
  'prop.choicePlaceholder':    'Texte du choix',
  'prop.explanationLabel':     'Explication (facultatif)',
  'prop.explanationPlaceholder': 'Justifiez la réponse avec des éléments du texte.',
  'prop.submit':               'Envoyer la proposition',
  'prop.submitting':           'Envoi…',
  'prop.success':              'Proposition reçue. Nous vous préviendrons si elle est adoptée.',
  'prop.needStem':             'Saisissez le texte de la question.',
  'prop.needChoices':          'Renseignez au moins deux choix.',
  'prop.needCorrect':          'Sélectionnez la bonne réponse.',
  'prop.myProposals':          'Mes propositions',
  'prop.none':                 'Pas encore de proposition.',
  'prop.status.pending':       'En cours d’examen',
  'prop.status.accepted':      'Adoptée',
  'prop.status.rejected':      'Refusée',
  'prop.aside.howLabel':       'Comment ça marche',
  'prop.aside.howBody':        'Proposition → examen par l’admin → si adoptée, elle rejoint la banque (publiée après contrôle qualité). Les meilleures questions sont adoptées plus souvent.',
  'prop.aside.rewardLabel':    'Récompenses',
  'prop.aside.rewardBody':     'Proposer rapporte des XP ; l’adoption rapporte plus d’XP et le badge « Proposeur de questions ».',
  'prop.difficulty':           'Difficulté suggérée (facultatif)',
  'prop.difficultyNone':       'Non spécifié',
  'detail.report':            'Rapport de réussite',
  'quiz.celebration.viewReport': 'Voir le rapport',
  'report.kicker':            "Rapport d'évaluation",
  'report.title':             'Rapport de réussite',
  'report.accuracy':          'Précision globale',
  'report.level':             'Compétence',
  'report.theta':             'Estimation de capacité (θ)',
  'report.thetaNote':         'Estimation relative — approximative, non calibrée',
  'report.attempted':         'Tentées',
  'report.unit':              'items',
  'report.naep':              'Domaines cognitifs NAEP',
  'report.lidia':             'Domaines LIDIA',
  'report.trend':             'Tendance de progression',
  'report.window':            'Période',
  'report.notAttempted':      'Non tentées',
  'report.empty':             "Pas encore d'évaluation",
  'report.emptySub':          'Résolvez des questions et votre maîtrise par domaine apparaîtra ici.',
  'report.startQuiz':         "Démarrer l'évaluation",
  'report.honestyLabel':      'À propos de ces mesures',
  'report.honestyBody':       "Les niveaux et θ sont des approximations relatives non calibrées, pas des scores standardisés (Lexile, notes d'examen).",
  'report.viewCredential':    'Voir le certificat',
  'report.viewAchievements':  'Réussites · XP',
  'report.retry':             'Réessayer',
  'report.naep.locate_recall':       'Localiser et mémoriser',
  'report.naep.integrate_interpret': 'Intégrer et interpréter',
  'report.naep.critique_evaluate':   'Critiquer et évaluer',
  'footer.larc':              'Comité de révision',
  'larc.kicker':             'Confiance et transparence',
  'larc.title':              'Comité de révision LARC',
  'larc.lead':               'Chaque item est généré par IA puis passe une révision en plusieurs étapes. LARC rend ce processus public.',
  'larc.missionLabel':       'Mission',
  'larc.missionBody':        "Nous garantissons l'équité et la précision de l'évaluation. Les items générés par IA sont vérifiés par des humains et des modèles croisés pour une mesure fiable.",
  'larc.criteriaLabel':      'Critères de révision',
  'larc.crit1':              "Preuve — la réponse est-elle réellement fondée sur le texte ?",
  'larc.crit2':              'Clarté — l\'énoncé et les choix sont-ils sans ambiguïté ?',
  'larc.crit3':              'Discrimination — les distracteurs sont-ils plausibles et la clé unique ?',
  'larc.crit4':              'Niveau et biais — adapté au niveau et sans biais ?',
  'larc.statsLabel':         'Activité de révision',
  'larc.stApproved':         'Approuvés',
  'larc.stPending':          "File LARC",
  'larc.stReviewed':         'Révisés par IA',
  'larc.stRejected':         'Rejetés',
  'larc.stTotal':            'Items au total',
  'larc.stReports':          'Signalements résolus',
  'larc.note':               'Seuls les totaux agrégés sont affichés — aucune donnée personnelle ni contenu d\'item.',
  'prelearn.title':          'Pré-apprentissage',
  'prelearn.subtitle':       'Contexte avant de lire',
  'prelearn.books':          'Acheter · librairies',
  'prelearn.media':          'Vidéo · contexte',
  'prelearn.note':           'Ouvre une recherche sur un site externe.',
  'creds.summaryLabel':      'Certification globale de compétence',
  'creds.summaryTotal':      'Certifications',
  'creds.summaryBooks':      'Livres couverts',
  'creds.summaryHighest':    'Niveau max',
  'nav.vault':               'Coffre',
  'vault.title':             'Coffre de connaissances',
  'vault.subtitle':          'Rassemblez notes, citations et idées',
  'vault.kindMemo':          'Note',
  'vault.kindQuote':         'Citation',
  'vault.kindInsight':       'Idée',
  'vault.kindFeedback':      'Retour',
  'vault.contentPlaceholder': 'Notez ce que vous voulez retenir',
  'vault.tagsPlaceholder':   'Étiquettes (séparées par des virgules)',
  'vault.add':               'Enregistrer',
  'vault.searchPlaceholder': 'Rechercher du contenu',
  'vault.allKinds':          'Toutes',
  'vault.empty':             'Aucune note enregistrée.',
  'vault.delete':            'Supprimer',
  'vault.added':             'Enregistré.',
  'vault.deleted':           'Supprimé.',
  'vault.needContent':       'Saisissez du contenu.',
  'vault.export':            'Exporter',
  'vault.exportMd':          'Markdown',
  'vault.exportJson':        'JSON',
  'verify.title':            'Vérification de certificat',
  'verify.checking':         'Vérification…',
  'verify.valid':            'Certificat valide',
  'verify.invalid':          'Certificat invalide ou révoqué',
  'verify.notFound':         'Certificat introuvable',
  'verify.tier':             'Niveau',
  'verify.issued':           'Émis le',
  'verify.note':             "Vérifié avec les registres d'émission BookQuest. Aucune donnée personnelle n'est exposée.",
};

export default fr;
